'use client';

import {
	AMM_RESERVE_PRECISION_EXP,
	BASE_PRECISION_EXP,
	BigNum,
	BN,
	PRICE_PRECISION,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
} from '@drift-labs/sdk';
import {
	COMMON_UI_UTILS,
	MarketId,
	TRADING_UTILS,
	UIOrderType,
} from '@drift/common';
import React, { useMemo } from 'react';
import ValueDisplay from 'src/components/ValueDisplay';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import NumLib from 'src/utils/NumLib';
import { toNotional } from 'src/utils/formatting';

import {
	useCurrentMarketStepSizeBn,
	useMarketStepSize,
} from 'src/hooks/useMarketStepSize';
import { calculateAveragePrice } from 'src/utils/scaledOrders';
import usePreviewOrder from 'src/components/TradeForm/hooks/usePreviewOrder';
import useUserAccountIsReady from 'src/hooks/useUserAccountIsReady';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { ZERO } from 'src/constants/constants';
import useTradeFormEstimatedLiquidationPrice from 'src/hooks/Liquidations/useTradeFormEstimatedLiquidationPrice';
import SwiftOfflineIndicator from 'src/components/SwiftOfflineIndicator';
import FeeTierValueDisplay from 'src/components/TradeForm/FeeTierValueDisplay';
import { useCurrentAccountPositions } from 'src/hooks/query/accountTradingData/usePositions';
import LiquidationPriceDisplay from './LiquidationPriceDisplay';
import { PRICE_FALLBACK_DISPLAY_VALUE } from 'src/components/TradeForm/consts';
import { getMarketPriceDecimals } from 'src/constants/marketDecimalConfig';
import BottomDrawerSlippageToleranceInput from 'src/components/TradeForm/SlippageToleranceInput/BottomDrawerSlippageToleranceInput';
import CollapsibleSlippageToleranceInput from 'src/components/TradeForm/SlippageToleranceInput/CollapsibleSlippageToleranceInput';
import LimitOraclePriceDisplay from 'src/components/TradeForm/TradeDetails/LimitOraclePriceDisplay';
import EstimatedProfit from '../EstimatedProfit';
import { MarginRequiredTradeDetails } from './MarginRequiredTradeDetails';
import useSizeInputSource from 'src/components/TradeForm/SizeInput/hooks/useSizeInputSource';
import useDriftClient from 'src/hooks/useDriftClient';
import { roundToStepSizeIfLargeEnough, TRADE_PRECISION } from 'src/utils/math';

interface PerpTradeDetailsProps {
	orderType: UIOrderType;
	showSlippageTolerance?: boolean;
	marginRequired?: BN;
}

const PerpTradeDetails = ({
	orderType,
	showSlippageTolerance,
	marginRequired,
}: PerpTradeDetailsProps) => {
	const isMobileScreenSize = useIsMobileScreenSize();
	const tradeForm = useDriftStore((s) => s.tradeForm);
	const [sizeInputSource] = useSizeInputSource();

	const {
		baseSizeStringValue,
		quoteSizeStringValue,
		priceBoxStringValue,
		secondaryPriceBoxStringValue,
		scaledOrders: { sizeDistribution, orderCount },
	} = useDriftStore((s) => s.tradeForm);

	const selectedMarketIndex = useDriftStore(
		(s) => s.selectedMarket?.marketId?.marketIndex
	);
	const selectedMarket = useDriftStore((s) => s.selectedMarket.current);
	const side = tradeForm.side;

	const baseAssetSymbol = selectedMarket?.baseAssetSymbol() || '';
	const marketDecimals = getMarketPriceDecimals(baseAssetSymbol);

	const currentAccountPositions = useCurrentAccountPositions();
	const currentPosition = useMemo(
		() =>
			currentAccountPositions.find(
				(pos) => pos.marketIndex === selectedMarketIndex
			) ?? null,
		[currentAccountPositions, selectedMarketIndex]
	);

	const quoteSize = Number(quoteSizeStringValue);
	const marketId = selectedMarket?.marketId ?? MarketId.createPerpMarket(0);

	const driftClient = useDriftClient();
	const stepSizeDecimalPlaces = driftClient
		? TRADING_UTILS.getMarketStepSizeDecimals(driftClient, marketId)
		: TRADE_PRECISION;

	const previewOrderParams = usePreviewOrder();

	const { entryPrice } = previewOrderParams.priceImpact || {};

	const stepSize = useMarketStepSize(marketId);

	const priceBoxBigNum = BigNum.fromPrint(
		priceBoxStringValue,
		PRICE_PRECISION_EXP
	);

	const getEntryPrice = () =>
		BigNum.from(entryPrice ? entryPrice : 0, PRICE_PRECISION_EXP);

	const userAccountIsReady = useUserAccountIsReady();

	let currentLiquidationPrice = BigNum.from(
		currentPosition?.liqPrice ?? ZERO,
		PRICE_PRECISION_EXP
	).toNum();

	currentLiquidationPrice =
		currentLiquidationPrice < 0 ? 0 : currentLiquidationPrice;

	const liqPriceAfter = useTradeFormEstimatedLiquidationPrice();

	const previousBaseValue = BigNum.from(
		currentPosition?.baseSize,
		AMM_RESERVE_PRECISION_EXP
	);

	const baseSize = BigNum.fromPrint(
		baseSizeStringValue ? baseSizeStringValue : '0',
		AMM_RESERVE_PRECISION_EXP
	);

	const afterBaseValue = previousBaseValue.add(
		baseSize.mul(BigNum.from(side === 'buy' ? new BN(1) : new BN(-1)))
	);

	const previousDirection = previousBaseValue.lt(ZERO)
		? 'SHORT'
		: previousBaseValue.eq(ZERO)
			? ''
			: 'LONG';

	const afterDirection = afterBaseValue.lt(ZERO)
		? 'SHORT'
		: afterBaseValue.eq(ZERO)
			? ''
			: 'LONG';

	const currentStepSize = useCurrentMarketStepSizeBn();
	const currentStepSizeBigNum = BigNum.from(
		currentStepSize,
		BASE_PRECISION_EXP
	);
	const scaledOrdersAveragePrice: BN | null = useMemo(() => {
		try {
			return orderType === 'scaledOrders'
				? calculateAveragePrice(
						sizeDistribution,
						baseSize,
						currentStepSizeBigNum,
						orderCount,
						priceBoxBigNum.val,
						BigNum.fromPrint(secondaryPriceBoxStringValue, PRICE_PRECISION_EXP)
							.val
					)
				: null;
		} catch {
			return null;
		}
	}, [
		sizeDistribution,
		baseSize.val,
		orderCount,
		stepSize,
		priceBoxBigNum,
		secondaryPriceBoxStringValue,
	]);

	const limitExitSize = BigNum.fromPrint(
		baseSizeStringValue,
		BASE_PRECISION_EXP
	);
	const currentPositionBaseSize = currentPosition
		? BigNum.from(currentPosition.baseSize, BASE_PRECISION_EXP).abs()
		: BigNum.zero(BASE_PRECISION_EXP);

	const orderIsIncreasingPosition =
		currentPosition &&
		((currentPosition.direction === 'short' && side === 'sell') ||
			(currentPosition.direction === 'long' && side === 'buy'));

	return (
		<div className="flex w-full flex-col gap-y-sm">
			{showSlippageTolerance &&
				(isMobileScreenSize ? (
					<BottomDrawerSlippageToleranceInput />
				) : (
					<CollapsibleSlippageToleranceInput />
				))}

			<MarginRequiredTradeDetails
				baseSizeStringValue={baseSizeStringValue}
				selectedMarketIndex={selectedMarketIndex}
				estEntryPrice={getEntryPrice().val}
				marginRequired={
					marginRequired
						? BigNum.from(marginRequired, QUOTE_PRECISION_EXP)
						: undefined
				}
			/>

			{!isMobileScreenSize && (
				<ValueDisplay.Default
					label={`Order ${sizeInputSource === 'base' ? 'Value' : 'Size'}`}
					value={
						sizeInputSource === 'base'
							? toNotional(quoteSize)
							: `${baseSize.prettyPrint(
									false,
									undefined,
									stepSizeDecimalPlaces
								)} ${baseAssetSymbol}`
					}
				/>
			)}

			{orderType === 'oracleLimit' && <LimitOraclePriceDisplay />}

			{!isMobileScreenSize &&
				orderType === 'limit' &&
				currentPosition &&
				limitExitSize.lte(currentPositionBaseSize) &&
				limitExitSize.gtZero() &&
				!orderIsIncreasingPosition && (
					<EstimatedProfit
						marketId={marketId}
						currentPositionSize={currentPositionBaseSize}
						currentPositionEntryPrice={BigNum.from(
							currentPosition.entryPrice,
							PRICE_PRECISION_EXP
						)}
						currentPositionDirection={currentPosition.direction}
						side={side}
						limitExitSize={limitExitSize}
						limitExitPrice={BigNum.fromPrint(
							priceBoxStringValue,
							PRICE_PRECISION_EXP
						)}
					/>
				)}

			<LiquidationPriceDisplay
				orderType={orderType}
				liqPriceBefore={currentLiquidationPrice}
				liqPriceBeforeString={
					currentLiquidationPrice !== 0
						? COMMON_UI_UTILS.trimTrailingZeros(
								toNotional(currentLiquidationPrice, marketDecimals)
							)
						: PRICE_FALLBACK_DISPLAY_VALUE
				}
				liqPriceAfter={liqPriceAfter}
				liqPriceAfterString={
					baseSizeStringValue && baseSizeStringValue !== '0'
						? liqPriceAfter !== 0
							? COMMON_UI_UTILS.trimTrailingZeros(
									toNotional(liqPriceAfter, marketDecimals)
								)
							: PRICE_FALLBACK_DISPLAY_VALUE
						: ''
				}
				userAccountIsReady={userAccountIsReady}
				selectedMarketIndex={selectedMarketIndex}
				baseSize={baseSize}
				isMobileScreenSize={isMobileScreenSize}
			/>

			{!isMobileScreenSize && (
				<ValueDisplay.ValueChange
					label="Position"
					previousValue={previousBaseValue.abs().toNum()}
					afterValue={afterBaseValue.abs().toNum()}
					previousValuePrint={`${previousBaseValue
						.abs()
						.toNum()
						.toLocaleString('en-US', {
							maximumSignificantDigits: 5,
						})} ${previousDirection}`}
					afterValuePrint={`${roundToStepSizeIfLargeEnough(
						afterBaseValue.abs().toNum().toLocaleString('en-US', {
							maximumSignificantDigits: 5,
						}),
						stepSize
					)} ${afterDirection}`}
					forceWhite
				/>
			)}

			<FeeTierValueDisplay />

			{!isMobileScreenSize && scaledOrdersAveragePrice && (
				<div className="flex w-full flex-col gap-y-sm">
					<ValueDisplay.Default
						label="Average price"
						value={`$${NumLib.formatNum.toDisplayPrice(
							NumLib.formatBn.toRawNum(
								scaledOrdersAveragePrice,
								PRICE_PRECISION
							)
						)}`}
					/>

					<SwiftOfflineIndicator className="ml-auto" />
				</div>
			)}
		</div>
	);
};

export default React.memo(PerpTradeDetails);
